import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { useAppSelector } from 'src/redux/app/app.hooks'
import { selectIsNavOpen } from 'src/redux/nav'
import { selectIsHolidayAnimationEnabled } from 'src/redux/holidayAnimation'

const EMOJIS = ['🌸', '🌷', '🪷', '🦋', '🌺']

interface HolidayAnimationItemData {
    id: number
    left: number
    delay: number
    duration: number
    size: number
    createdAt: number
    emoji: string
}

export const HolidayAnimation = () => {
    const isHolidayAnimationEnabled = useAppSelector(selectIsHolidayAnimationEnabled)
    const isNavOpen = useAppSelector(selectIsNavOpen)
    const [items, setItems] = useState<HolidayAnimationItemData[]>([])
    const nextIdRef = useRef(0)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null)
    const isActiveRef = useRef(false)
    const itemsRef = useRef<HolidayAnimationItemData[]>([])

    // Keep ref in sync with state for cleanup logic
    useEffect(() => {
        itemsRef.current = items
    }, [items])

    useEffect(() => {
        // Set the active flag immediately - CRITICAL: do this first
        isActiveRef.current = isHolidayAnimationEnabled

        // Always clear any existing intervals FIRST - before anything else
        const currentInterval = intervalRef.current
        if (currentInterval) {
            clearInterval(currentInterval)
            intervalRef.current = null
        }
        const currentCleanupInterval = cleanupIntervalRef.current
        if (currentCleanupInterval) {
            clearInterval(currentCleanupInterval)
            cleanupIntervalRef.current = null
        }

        // If disabled, stop here
        if (!isHolidayAnimationEnabled) {
            isActiveRef.current = false

            // Clean up remaining items after their animations complete
            if (itemsRef.current.length > 0) {
                const maxFinishTime = Math.max(...itemsRef.current.map(s => (s.duration + s.delay) * 1000))
                const timeout = setTimeout(() => {
                    setItems([])
                }, maxFinishTime + 100)

                return () => {
                    clearTimeout(timeout)
                    isActiveRef.current = false
                }
            }

            return () => {
                isActiveRef.current = false
            }
        }

        // Generate items continuously only when enabled
        const newInterval = setInterval(() => {
            // Check the flag FIRST - must be first check
            if (!isActiveRef.current) {
                clearInterval(newInterval)
                intervalRef.current = null
                return
            }

            // Create and add item only if still active
            const newItem: HolidayAnimationItemData = {
                id: nextIdRef.current++,
                left: Math.random() * 100,
                delay: 0,
                duration: 6 + Math.random() * 3,
                size: 8 + Math.random() * 8,
                createdAt: Date.now(), // Track creation time for cleanup
                emoji: EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
            }

            setItems(prev => {
                // Increase limit to accommodate longer animations
                // With 6-9 second durations and 200ms generation interval,
                // we could have up to 45 items at once (9s / 0.2s = 45)
                // Using 60 to provide buffer for wiggle animation
                return [...prev, newItem].slice(-60)
            })
        }, 200)

        intervalRef.current = newInterval

        // Periodically remove items that have finished their animation
        // This prevents memory accumulation from old items
        const cleanupInterval = setInterval(() => {
            if (!isActiveRef.current) {
                return
            }

            const now = Date.now()
            setItems(prev => {
                // Remove items that have been animating longer than their duration
                // Add 1 second buffer to ensure animation has completed
                return prev.filter(item => {
                    const elapsed = (now - item.createdAt) / 1000
                    return elapsed < item.duration + 1
                })
            })
        }, 1000) // Check every second

        cleanupIntervalRef.current = cleanupInterval

        // Cleanup - set flag to false and clear intervals
        return () => {
            isActiveRef.current = false
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
            if (cleanupIntervalRef.current) {
                clearInterval(cleanupIntervalRef.current)
                cleanupIntervalRef.current = null
            }
        }
    }, [isHolidayAnimationEnabled])

    // Always render the container if there are items, even when disabled
    if (items.length === 0) {
        return null
    }

    const content = (
        <ItemsContainer $sidebarWidth={isNavOpen ? 260 : 70}>
            {items.map(item => (
                <FallingItem
                    key={item.id}
                    $left={item.left}
                    $delay={item.delay}
                    $duration={item.duration}
                    $size={item.size}
                >
                    {item.emoji}
                </FallingItem>
            ))}
        </ItemsContainer>
    )

    // Render using portal to avoid DOM manipulation conflicts
    return createPortal(content, document.body)
}

const fall = keyframes`
    0% {
        transform: translate3d(0, 0, 0) rotate(0deg);
        opacity: 1;
    }
    25% {
        transform: translate3d(-8px, calc((100vh + 50px) * 0.25), 0) rotate(90deg);
        opacity: 1;
    }
    50% {
        transform: translate3d(8px, calc((100vh + 50px) * 0.5), 0) rotate(180deg);
        opacity: 1;
    }
    75% {
        transform: translate3d(-6px, calc((100vh + 50px) * 0.75), 0) rotate(270deg);
        opacity: 1;
    }
    100% {
        transform: translate3d(0, calc(100vh + 50px), 0) rotate(360deg);
        opacity: 1;
    }
`

const FallingItem = styled.div<{ $left: number; $delay: number; $duration: number; $size: number }>`
    position: absolute;
    left: ${props => props.$left}%;
    top: -50px;
    font-size: ${props => props.$size}px;
    opacity: 1;
    pointer-events: none;
    will-change: transform;
    contain: layout style paint;
    animation: ${fall} ${props => props.$duration}s linear forwards;
    animation-delay: ${props => props.$delay}s;
    user-select: none;
`

const ItemsContainer = styled.div<{ $sidebarWidth: number }>`
    position: fixed;
    top: 70px;
    left: 0;
    width: ${props => props.$sidebarWidth}px;
    height: calc(100vh - 70px);
    overflow: hidden;
    pointer-events: none;
    z-index: 1;
    will-change: contents;
    contain: layout style paint;
`
